import { COMPANY, REPORT } from '../../config/company';
import { LegalLayout } from './LegalLayout';

// Must match what the site and the report page really store: the notice's
// key in components/CookieNotice.tsx, and sessionStorage in
// ../hethera-user-dashboard (models/report/session.ts, components/Fun/motion.tsx).
export function CookiePolicyPage() {
  return (
    <LegalLayout
      path="/cookies"
      title="Cookie Policy"
      intro="Hethera's websites don't use cookies for advertising or analytics. This page lists the little they do store in your browser, and why."
    >
      <h2>The short version</h2>
      <p>
        We don't set any cookies on {COMPANY.brandName}'s websites. We don't use analytics, advertising
        or social media trackers, and we don't follow you across other websites. The only things kept
        in your browser are what the pages need to work, listed below.
      </p>

      <h2>Cookies and browser storage</h2>
      <p>
        A cookie is a small file a website saves in your browser and gets back on later visits.
        Websites can also keep small notes in your browser's local storage, which stays until you
        clear it, or session storage, which is cleared when you close the tab. The law treats them
        the same way, so this policy covers all three.
      </p>

      <h2>What we store</h2>
      <div className="legal-table">
        <table>
          <thead>
            <tr>
              <th scope="col">Where</th>
              <th scope="col">What and why</th>
              <th scope="col">How long</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>This website</td>
              <td>
                <code>hethera.cookieNotice</code> (local storage): remembers that you closed the cookie
                notice, so it doesn't appear on every page.
              </td>
              <td>Until you clear your browser's data</td>
            </tr>
            <tr>
              <td>Spending report page ({REPORT.host})</td>
              <td>
                A session key (session storage) that keeps your report open if you refresh the page,
                without opening the one-time link again.
              </td>
              <td>
                Until you close the tab; it stops working after {REPORT.sessionMinutes} minutes
              </td>
            </tr>
            <tr>
              <td>Spending report page</td>
              <td>
                A note (session storage) that the opening celebration has already played, so it plays
                once.
              </td>
              <td>Until you close the tab</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        These are strictly necessary for the pages to work as you'd expect, so they don't need your
        consent. None of them identifies you or is shared with anyone.
      </p>

      <h2>Other services the pages use</h2>
      <ul>
        <li>
          <strong>Google Fonts.</strong> Our pages load their typefaces from Google. Like any website
          your browser contacts, Google receives your internet address and browser type to send the
          files. Google Fonts doesn't set cookies.
        </li>
        <li>
          <strong>Our hosting provider</strong> keeps standard server logs (internet address, the page
          asked for, browser type and time) to deliver the site and keep it secure.
        </li>
        <li>
          <strong>Paystack.</strong> When you add a card, you enter it on Paystack's own secure page,
          not ours. Paystack may use cookies there for security and fraud prevention, under its own
          policies.
        </li>
        <li>
          <strong>WhatsApp.</strong> “Chat on WhatsApp” buttons and the QR code open WhatsApp, which
          Meta runs under its own policies.
        </li>
      </ul>

      <h2>Your choices</h2>
      <p>
        You can clear or block cookies and site data in your browser's settings at any time. Everything
        on this website still works if you do; the cookie notice will just show again. On the spending
        report page, blocking storage means a refresh closes your report.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If we ever want to use cookies or similar tools that aren't strictly necessary, such as
        analytics, we will ask for your consent before using them and update this page first. For how
        we handle the rest of your personal data, see our <a href="/privacy">Privacy Policy</a>.
      </p>
    </LegalLayout>
  );
}
